import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  computed,
  inject,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';

import { Wareneingang, WareneingangFaelligkeit } from '../../models/wareneingang.model';
import { WareneingangStatus } from '../../models/wareneingang-status.enum';
import { WareneingaengeService } from '../../services/wareneingaenge.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, timer } from 'rxjs';

type StatusFilter = 'HANDLUNGSBEDARF' | 'ALLE' | WareneingangStatus;
type SortColumn = 'filiale' | 'status' | 'positionen';
type SortDirection = 'asc' | 'desc';

interface SortState {
  column: SortColumn | null;
  direction: SortDirection;
}

@Component({
  selector: 'app-wareneingaenge-uebersicht',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  templateUrl: './wareneingaenge-uebersicht.component.html',
  styleUrl: './wareneingaenge-uebersicht.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WareneingaengeUebersichtComponent {
  private readonly wareneingaengeService = inject(WareneingaengeService);
  private readonly router = inject(Router);
  private readonly elementRef = inject(ElementRef<HTMLElement>);

  private readonly destroyRef = inject(DestroyRef);
  private readonly currentTime = signal(Date.now());

  protected readonly WareneingangStatus = WareneingangStatus;

  protected readonly selectedFiliale = signal('ALLE');
  protected readonly selectedFilter = signal<StatusFilter>(WareneingangStatus.Offen);
  protected readonly sortState = signal<SortState>({
    column: null,
    direction: 'asc',
  });
  protected readonly isFilialeDropdownOpen = signal(false);
  protected readonly wareneingaenge = signal<Wareneingang[]>([]);

  protected readonly isLoading = signal(true);
  protected readonly skeletonSummaryCards = Array.from({ length: 4 });
  protected readonly skeletonFilterTabs = Array.from({ length: 5 });
  protected readonly skeletonRows = Array.from({ length: 6 });

  protected readonly filialen = computed(() => {
    const filialenMap = new Map<string, string>();

    for (const wareneingang of this.wareneingaenge()) {
      filialenMap.set(
        String(
          wareneingang.filialNummer
        ),
        wareneingang.filialeName
      );
    }

    return [
      { value: 'ALLE', label: 'Alle Filialen' },
      ...Array.from(filialenMap.entries()).map(([value, name]) => ({
        value,
        label: name
          ? `Filiale ${value} - ${name}`
          : `Filiale ${value}`,
      })),
    ];
  });

  protected readonly selectedFilialeLabel = computed(() => {
    return (
      this.filialen().find((filiale) => filiale.value === this.selectedFiliale())?.label ??
      'Alle Filialen'
    );
  });

  protected readonly filialeFilteredWareneingaenge =
    computed(() => {
      const selectedFiliale =
        this.selectedFiliale();

      if (selectedFiliale === 'ALLE') {
        return this.wareneingaenge();
      }

      return this.wareneingaenge().filter(
        (wareneingang) =>
          String(
            wareneingang.filialNummer
          ) === selectedFiliale
      );
    });

  protected readonly filteredWareneingaenge = computed(() => {
    const filteredWareneingaenge = this.filialeFilteredWareneingaenge().filter(
      (wareneingang) => this.matchesFilter(wareneingang, this.selectedFilter())
    );

    return this.sortWareneingaenge(filteredWareneingaenge);
  });

  // Summary Cards: immer über alle Filialen
  protected readonly summaryInBearbeitungCount = computed(
    () =>
      this.wareneingaenge().filter(
        (entry) => entry.status === WareneingangStatus.InBearbeitung
      ).length
  );

  protected readonly summaryOffenCount = computed(
    () =>
      this.wareneingaenge().filter(
        (entry) => entry.status !== WareneingangStatus.Abgeschlossen
      ).length
  );

  protected readonly summaryHandlungsbedarfCount = computed(() =>
    Math.max(
      this.summaryOffenCount() - this.summaryInBearbeitungCount(),
      0
    )
  );

  protected readonly summaryOffenePositionenGesamt = computed(() =>
    this.wareneingaenge().reduce(
      (sum, entry) => sum + entry.offenePositionen,
      0
    )
  );


  // Filter Tabs, Tabelle und Footer: abhängig von der ausgewählten Filiale
  protected readonly inBearbeitungCount = computed(
    () =>
      this.filialeFilteredWareneingaenge().filter(
        (entry) => entry.status === WareneingangStatus.InBearbeitung
      ).length
  );

  protected readonly offenCount = computed(
    () =>
      this.filialeFilteredWareneingaenge().filter(
        (entry) => entry.status !== WareneingangStatus.Abgeschlossen
      ).length
  );

  protected readonly handlungsbedarfCount = computed(() =>
    Math.max(
      this.offenCount() - this.inBearbeitungCount(),
      0
    )
  );

  protected readonly abgeschlossenCount = computed(
    () =>
      this.filialeFilteredWareneingaenge().filter(
        (entry) => entry.status === WareneingangStatus.Abgeschlossen
      ).length
  );

  protected readonly offenePositionenGesamt = computed(() =>
    this.filialeFilteredWareneingaenge().reduce(
      (sum, entry) => sum + entry.offenePositionen,
      0
    )
  );

  protected readonly totalCount = computed(
    () => this.offenCount() + this.abgeschlossenCount()
  );

  protected readonly erledigtCount = computed(() => this.abgeschlossenCount());

  protected readonly erledigtPercent = computed(() => {
    const total = this.totalCount();

    if (!total) {
      return 0;
    }

    return Math.round((this.erledigtCount() / total) * 100);
  });

  protected readonly statusFilters = computed(() => [
    {
      label: 'Handlungsbedarf',
      value: 'HANDLUNGSBEDARF' as const,
      count: this.handlungsbedarfCount(),
    },
    {
      label: 'Offen',
      value: WareneingangStatus.Offen,
      count: this.offenCount(),
    },
    {
      label: 'In Bearbeitung',
      value: WareneingangStatus.InBearbeitung,
      count: this.inBearbeitungCount(),
    },
    {
      label: 'Abgeschlossen',
      value: WareneingangStatus.Abgeschlossen,
      count: this.abgeschlossenCount(),
    },
    {
      label: 'Alle',
      value: 'ALLE' as const,
      count: this.totalCount(),
    },
  ]);

  constructor() {

    const now = Date.now();
    const millisecondsUntilNextMinute =
      60_000 - (now % 60_000);

    timer(millisecondsUntilNextMinute, 60_000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.currentTime.set(Date.now());
      });

    this.isLoading.set(true);

    this.wareneingaengeService
      .getWareneingaenge()
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (wareneingaenge) => {
          this.wareneingaenge.set(wareneingaenge);
        },
        error: () => {
          this.wareneingaenge.set([]);
        },
      });
  }

  @HostListener('document:click', ['$event'])
  protected closeFilialeDropdownOnOutsideClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      this.isFilialeDropdownOpen.set(false);
    }
  }

  @HostListener('document:keydown.escape')
  protected closeFilialeDropdownOnEscape(): void {
    this.isFilialeDropdownOpen.set(false);
  }

  protected toggleFilialeDropdown(): void {
    this.isFilialeDropdownOpen.update((state) => !state);
  }

  protected selectFiliale(filialeValue: string): void {
    this.selectedFiliale.set(filialeValue);
    this.isFilialeDropdownOpen.set(false);
  }

  protected setFilter(filter: StatusFilter): void {
    this.selectedFilter.set(filter);
  }

  protected setSort(column: SortColumn): void {
    const currentSort = this.sortState();

    const firstDirection: SortDirection =
      column === 'positionen' ? 'desc' : 'asc';

    const secondDirection: SortDirection =
      firstDirection === 'asc' ? 'desc' : 'asc';

    // First click on a new column
    if (currentSort.column !== column) {
      this.sortState.set({
        column,
        direction: firstDirection,
      });

      return;
    }

    // Second click on the same column
    if (currentSort.direction === firstDirection) {
      this.sortState.set({
        column,
        direction: secondDirection,
      });

      return;
    }

    // Third click: restore the original backend order
    this.sortState.set({
      column: null,
      direction: 'asc',
    });
  }

  protected isSortedBy(column: SortColumn): boolean {
    return this.sortState().column === column;
  }

  protected getSortIcon(column: SortColumn): string {
    if (!this.isSortedBy(column)) {
      return 'unfold_more';
    }

    return this.sortState().direction === 'asc'
      ? 'keyboard_arrow_up'
      : 'keyboard_arrow_down';
  }

  protected isSummaryActive(filter: StatusFilter): boolean {
    return this.selectedFilter() === filter;
  }

  protected openWareneingang(wareneingang: Wareneingang): void {
    this.router.navigate(['/wareneingaenge', wareneingang.id]);
  }

  protected getStatusLabel(status: WareneingangStatus): string {
    switch (status) {
      case WareneingangStatus.Offen:
        return 'Offen';
      case WareneingangStatus.InBearbeitung:
        return 'In Bearbeitung';
      case WareneingangStatus.Abgeschlossen:
        return 'Abgeschlossen';
    }
  }

  protected getStatusClass(status: WareneingangStatus): string {
    switch (status) {
      case WareneingangStatus.Offen:
        return 'status-offen';
      case WareneingangStatus.InBearbeitung:
        return 'status-in-bearbeitung';
      case WareneingangStatus.Abgeschlossen:
        return 'status-abgeschlossen';
    }
  }

  protected getActionLabel(wareneingang: Wareneingang): string {
    if (wareneingang.status === WareneingangStatus.Abgeschlossen) {
      return 'Ansehen';
    }

    return wareneingang.status === WareneingangStatus.Offen ? 'Erfassen' : 'Weiter';
  }

  protected getLiveFaelligkeitStatus(
    wareneingang: Wareneingang
  ): WareneingangFaelligkeit {
    if (wareneingang.status === WareneingangStatus.Abgeschlossen) {
      return wareneingang.faelligkeitStatus;
    }

    const deadline = this.getDeadlineTimestamp(wareneingang);

    if (deadline === null) {
      return wareneingang.faelligkeitStatus;
    }

    if (deadline <= this.currentTime()) {
      return 'UEBERFAELLIG';
    }

    const now = new Date(this.currentTime());
    const deadlineDate = new Date(deadline);

    const isDueToday =
      deadlineDate.getFullYear() === now.getFullYear() &&
      deadlineDate.getMonth() === now.getMonth() &&
      deadlineDate.getDate() === now.getDate();

    return isDueToday ? 'HEUTE' : 'GEPLANT';
  }

  protected getLiveFaelligkeitLabel(
    wareneingang: Wareneingang
  ): string {
    switch (this.getLiveFaelligkeitStatus(wareneingang)) {
      case 'UEBERFAELLIG':
        return 'Überfällig';

      case 'HEUTE':
        return 'Heute fällig';

      case 'GEPLANT':
        return 'Geplant';
    }
  }

  protected getFaelligkeitRestzeit(
    wareneingang: Wareneingang
  ): string | null {
    if (wareneingang.status === WareneingangStatus.Abgeschlossen) {
      return null;
    }

    const faelligkeitStatus =
      this.getLiveFaelligkeitStatus(wareneingang);

    if (
      faelligkeitStatus !== 'HEUTE' &&
      faelligkeitStatus !== 'UEBERFAELLIG'
    ) {
      return null;
    }

    const deadline = this.getDeadlineTimestamp(wareneingang);

    if (deadline === null) {
      return null;
    }

    const difference = Math.max(
      deadline - this.currentTime(),
      0
    );

    return `Noch ${this.formatDuration(difference)} Std.`;
  }

  protected getFaelligkeitClass(faelligkeit: WareneingangFaelligkeit): string {
    switch (faelligkeit) {
      case 'UEBERFAELLIG':
        return 'faelligkeit-ueberfaellig';
      case 'HEUTE':
        return 'faelligkeit-heute';
      default:
        return 'faelligkeit-normal';
    }
  }

  protected formatDisplayDate(isoDate: string): string {
    const [year, month, day] = isoDate.split('-').map(Number);

    if (!year || !month || !day) {
      return isoDate;
    }

    const date = new Date(year, month - 1, day);

    const weekday = new Intl.DateTimeFormat('de-DE', {
      weekday: 'long',
    }).format(date);

    const formattedDate = new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(date);

    return `${weekday.charAt(0).toUpperCase()}${weekday.slice(1)}, ${formattedDate}`;
  }

  protected formatCompactDisplayDate(isoDate: string): string {
    const datePart = isoDate.split('T')[0];
    const [year, month, day] = datePart.split('-').map(Number);

    if (!year || !month || !day) {
      return this.formatDisplayDate(isoDate);
    }

    const date = new Date(year, month - 1, day);

    const weekday = new Intl.DateTimeFormat('de-DE', {
      weekday: 'short',
    }).format(date);

    const formattedDate = new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(date);

    return `${weekday}, ${formattedDate}`;
  }

  private getDeadlineTimestamp(
    wareneingang: Wareneingang
  ): number | null {
    const dateMatch = wareneingang.wareneingangTag.match(
      /^(\d{4})-(\d{2})-(\d{2})/
    );

    const timeMatch = wareneingang.bearbeitungsZeitBis.match(
      /^(\d{1,2}):(\d{2})/
    );

    if (!dateMatch || !timeMatch) {
      return null;
    }

    const [, year, month, day] = dateMatch;
    const [, hour, minute] = timeMatch;

    return new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      0,
      0
    ).getTime();
  }

  private formatDuration(
    durationInMilliseconds: number
  ): string {
    const totalMinutes = Math.ceil(
      durationInMilliseconds / 60_000
    );

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return (
      `${hours.toString().padStart(2, '0')}:` +
      `${minutes.toString().padStart(2, '0')}`
    );
  }

  private sortWareneingaenge(
    wareneingaenge: Wareneingang[]
  ): Wareneingang[] {
    const currentSort = this.sortState();

    if (currentSort.column === null) {
      return [...wareneingaenge];
    }

    return [...wareneingaenge].sort((first, second) => {
      if (currentSort.column === 'filiale') {
        const filialeCompare = first.filialeName.localeCompare(
          second.filialeName,
          'de',
          {
            sensitivity: 'base',
            numeric: true,
          }
        );

        if (filialeCompare !== 0) {
          return currentSort.direction === 'asc'
            ? filialeCompare
            : filialeCompare * -1;
        }

        return (
          first.filialNummer -
          second.filialNummer
        );
      }

      if (currentSort.column === 'status') {
        const statusCompare = this.compareStatus(
          first,
          second,
          currentSort.direction
        );

        if (statusCompare !== 0) {
          return statusCompare;
        }

        const positionCompare =
          second.offenePositionen - first.offenePositionen;

        if (positionCompare !== 0) {
          return positionCompare;
        }

        return first.filialeName.localeCompare(
          second.filialeName,
          'de'
        );
      }

      const positionCompare = this.compareOffenePositionen(
        first,
        second,
        currentSort.direction
      );

      if (positionCompare !== 0) {
        return positionCompare;
      }

      const statusCompare = this.compareStatus(
        first,
        second,
        'asc'
      );

      if (statusCompare !== 0) {
        return statusCompare;
      }

      return first.filialeName.localeCompare(
        second.filialeName,
        'de'
      );
    });
  }

  private compareStatus(
    first: Wareneingang,
    second: Wareneingang,
    direction: SortDirection
  ): number {
    const result =
      this.getStatusSortValue(first.status) - this.getStatusSortValue(second.status);

    return direction === 'asc' ? result : result * -1;
  }

  private compareOffenePositionen(
    first: Wareneingang,
    second: Wareneingang,
    direction: SortDirection
  ): number {
    const result = first.offenePositionen - second.offenePositionen;

    return direction === 'asc' ? result : result * -1;
  }

  private getStatusSortValue(status: WareneingangStatus): number {
    switch (status) {
      case WareneingangStatus.Offen:
        return 0;
      case WareneingangStatus.InBearbeitung:
        return 1;
      case WareneingangStatus.Abgeschlossen:
        return 2;
    }
  }

  private matchesFilter(
    wareneingang: Wareneingang,
    filter: StatusFilter
  ): boolean {
    if (filter === 'ALLE') {
      return true;
    }

    if (filter === 'HANDLUNGSBEDARF') {
      return wareneingang.status === WareneingangStatus.Offen;
    }

    if (filter === WareneingangStatus.Offen) {
      return wareneingang.status !== WareneingangStatus.Abgeschlossen;
    }

    return wareneingang.status === filter;
  }
}